import {
  UserDriverEditFormResponseAssociation,
  UserDriverEditFormResponseCarOwnership,
  UserDriverEditFormResponseCitizenship,
  UserDriverEditFormResponseDriver,
  UserDriverEditFormResponseFormOfEmployment,
  UserDriverEditFormResponseItem,
  UserDriverEditFormResponseLanguage,
  UserDriverEditFormResponseAddress,
  UserDriverEditFormResponseUser,
  UserDriverEditFormResponseFleetPartner,
  UserDriverEditFormResponseTaxiCorporation,
} from "../../../../api/raily/user/driver/edit-form/user-driver-edit-form.response";
import AddressType from "../../../../types/address-type";
import UserCitizenship from "../../../../types/user-citizenship";
import UserFormOfEmployment from "../../../../types/user-form-of-employment";
import UserLanguage from "../../../../types/user-language";
import UserVehicleOwnership from "../../../../types/user-vehicle-ownership-form";
import UserDriverEditForm, {
  UserDriverEditFormAddress,
  UserDriverEditFormAssociation,
  UserDriverEditFormDriver,
  UserDriverEditFormFleetPartner,
  UserDriverEditFormTaxiCorporation,
  UserDriverEditFormUser,
} from "./user-driver-edit-form";

const createFormOfEmployment = (
  formOfEmployment: UserDriverEditFormResponseFormOfEmployment,
): UserFormOfEmployment => {
  switch (formOfEmployment) {
    case UserDriverEditFormResponseFormOfEmployment.B2B:
      return UserFormOfEmployment.B2B;
    case UserDriverEditFormResponseFormOfEmployment.EMPLOYMENT:
      return UserFormOfEmployment.EMPLOYMENT;
  }
};

const createLanguage = (
  type: UserDriverEditFormResponseLanguage,
): UserLanguage => {
  switch (type) {
    case UserDriverEditFormResponseLanguage.BY:
      return UserLanguage.BY;
    case UserDriverEditFormResponseLanguage.CZ:
      return UserLanguage.CZ;
    case UserDriverEditFormResponseLanguage.DE:
      return UserLanguage.DE;
    case UserDriverEditFormResponseLanguage.LT:
      return UserLanguage.LT;
    case UserDriverEditFormResponseLanguage.PL:
      return UserLanguage.PL;
    case UserDriverEditFormResponseLanguage.RU:
      return UserLanguage.RU;
    case UserDriverEditFormResponseLanguage.SK:
      return UserLanguage.SK;
    case UserDriverEditFormResponseLanguage.UA:
      return UserLanguage.UA;
    case UserDriverEditFormResponseLanguage.EN:
      return UserLanguage.EN;
    case UserDriverEditFormResponseLanguage.ES:
      return UserLanguage.ES;
    case UserDriverEditFormResponseLanguage.FR:
      return UserLanguage.FR;
  }
};

const createLanguages = (
  languages: UserDriverEditFormResponseLanguage[],
): UserLanguage[] => {
  const requestLanguages: UserLanguage[] = languages.map(createLanguage);

  return requestLanguages;
};

const createCitizenship = (
  type: UserDriverEditFormResponseCitizenship,
): UserCitizenship => {
  switch (type) {
    case UserDriverEditFormResponseCitizenship.BY:
      return UserCitizenship.BY;
    case UserDriverEditFormResponseCitizenship.CZ:
      return UserCitizenship.CZ;
    case UserDriverEditFormResponseCitizenship.DE:
      return UserCitizenship.DE;
    case UserDriverEditFormResponseCitizenship.LT:
      return UserCitizenship.LT;
    case UserDriverEditFormResponseCitizenship.PL:
      return UserCitizenship.PL;
    case UserDriverEditFormResponseCitizenship.RU:
      return UserCitizenship.RU;
    case UserDriverEditFormResponseCitizenship.SK:
      return UserCitizenship.SK;
    case UserDriverEditFormResponseCitizenship.UA:
      return UserCitizenship.UA;
  }
};

const createCarOwnership = (
  type: UserDriverEditFormResponseCarOwnership,
): UserVehicleOwnership => {
  switch (type) {
    case UserDriverEditFormResponseCarOwnership.BUSINESS:
      return UserVehicleOwnership.BUSINESS;
    case UserDriverEditFormResponseCarOwnership.OWNER:
      return UserVehicleOwnership.OWNER;
  }
};

const createFleetPartner = (
  fleetPartner: UserDriverEditFormResponseFleetPartner,
): UserDriverEditFormFleetPartner => {
  return {
    name: fleetPartner.name,
    uuid: fleetPartner.id,
  };
};

const createFleetPartners = (
  fleetPartners: UserDriverEditFormResponseFleetPartner[],
): UserDriverEditFormFleetPartner[] => {
  return fleetPartners.map(createFleetPartner);
};

const createTaxiCorporation = (
  taxiCorporation: UserDriverEditFormResponseTaxiCorporation,
): UserDriverEditFormTaxiCorporation => {
  return {
    companyName: taxiCorporation.companyName,
    displayName: taxiCorporation.displayName,
    fleetPartners: createFleetPartners(taxiCorporation.fleetPartners),
    id: taxiCorporation.id,
  };
};

const createTaxiDriverAssociation = (
  association: UserDriverEditFormResponseAssociation,
): UserDriverEditFormAssociation => {
  return {
    companyName: association.companyName,
    apartment: association.apartment,
    contractType: createFormOfEmployment(association.contractType),
    country: association.country,
    description: association.description,
    fleetPartner: association.fleetPartner
      ? createFleetPartner(association.fleetPartner)
      : null,
    houseNo: association.houseNo,
    id: association.id,
    krsNo: association.krsNo,
    regon: association.regon,
    street: association.street,
    taxiCorporation: createTaxiCorporation(association.taxiCorporation),
    taxId: association.taxId,
    town: association.town,
    zipCode: association.zipCode,
  };
};

const createTaxiDriverAssociations = (
  associations: UserDriverEditFormResponseAssociation[],
): UserDriverEditFormAssociation[] => {
  return associations.map(createTaxiDriverAssociation);
};

const createUser = (
  user: UserDriverEditFormResponseUser,
): UserDriverEditFormUser => {
  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    mobilePrimary: user.mobilePrimary,
    mobilePrimaryPrefix: user.mobilePrimaryPrefix,
    mobileSecondary: user.mobileSecondary,
    mobileSecondaryPrefix: user.mobileSecondaryPrefix,
    username: user.username,
  };
};

const createAddress = (
  address: UserDriverEditFormResponseAddress,
  type: AddressType,
): UserDriverEditFormAddress => {
  return {
    apartment: address.apartment,
    country: address.country,
    description: address.description,
    houseNo: address.houseNo,
    lat: address.lat,
    lon: address.lon,
    street: address.street,
    town: address.town,
    type: type,
    zipCode: address.zipCode,
  };
};

const createAddresses = (
  startingAddress: UserDriverEditFormResponseAddress,
  mailingAddress: UserDriverEditFormResponseAddress,
): UserDriverEditFormAddress[] => {
  const requestAddresses: UserDriverEditFormAddress[] = [];

  requestAddresses.push(createAddress(startingAddress, AddressType.STARTING));
  requestAddresses.push(createAddress(mailingAddress, AddressType.MAILING));

  return requestAddresses;
};

const createDriver = (
  driver: UserDriverEditFormResponseDriver,
): UserDriverEditFormDriver => {
  return {
    birthDate: driver.birthDate,
    birthPlace: driver.birthPlace,
    carMake: driver.carMake,
    carModel: driver.carModel,
    carOwnership: createCarOwnership(driver.carOwnership),
    carProdYear: driver.carProdYear,
    carRegNo: driver.carRegNo,
    carSeatsNo: driver.carSeatsNo,
    citizenship: createCitizenship(driver.citizenship),
    experience: driver.experience,
    idNumber: driver.idNumber,
    languages: createLanguages(driver.languages),
  };
};

const create = (item: UserDriverEditFormResponseItem): UserDriverEditForm => {
  return {
    addresses: createAddresses(item.startingAddress, item.mailingAddress),
    associations: createTaxiDriverAssociations(item.associations),
    driver: createDriver(item.driver),
    user: createUser(item.user),
  };
};

const userDriverEditFormFactory = { create };

export default userDriverEditFormFactory;
